import { AbstractSession, sortSessionsByStartDate } from "@kvix/shared";
import { KvixIcon, Text } from "@kvix/ui";
import { Box, Divider, Grid, makeStyles, Tooltip } from "@material-ui/core";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  format,
  isEqual,
  isSameDay,
  isToday,
  parseJSON,
  startOfMonth,
  subMonths,
} from "date-fns";
import { sv } from "date-fns/locale";
import i18next from "i18next";
import React, { Fragment, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { animated, useSpring } from "react-spring";
import { isInstructor, KvixUserContext } from "../../../../contexts/user";
import { useInstructorSessions } from "../../../../hooks/session";
import { ScheduledSessionCard } from "../../../partials/ScheduledSessionCard";
import { MonthActivity } from "../../../partials/User/MonthActivity";

const useStyles = makeStyles((theme) => ({
  link: {
    color: theme.palette.primary.main,
  },
  activityDashboard: {
    "& > div:nth-of-type(1) svg": {
      cursor: "pointer",
      fontSize: 30,
      color: theme.palette.primary.main,
    },
    "& > div:nth-of-type(1) > div:nth-of-type(1)": {
      textTransform: "capitalize",
      width: 155,
      marginLeft: 24,
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
    "& > div:nth-of-type(1) > div:nth-of-type(2)": {
      padding: "14px 12px 10px 12px",
      [theme.breakpoints.down("xs")]: {
        marginLeft: "auto",
      },
    },
  },
}));

const SegmentTitle: React.FC<{
  tooltip?: string;
}> = (props) => {
  return (
    <Tooltip title={props.tooltip || ""} arrow placement="top">
      <Box display="inline-block">
        <Text variant="h4" unselectable>
          {props.children}
        </Text>
      </Box>
    </Tooltip>
  );
};

const WeekSchedule: React.FC<{
  sessions: AbstractSession[];
  emptyText?: React.ReactNode;
  label?: string;
  tooltip?: string;
}> = (props) => {
  const { t, ready } = useTranslation("general");
  const currentLanguage = i18next.language;
  const days = useMemo(() => {
    const now = new Date();
    const weekDays = eachDayOfInterval({
      start: now,
      end: addDays(now, 6),
    });

    const week = weekDays.map((day) => {
      const today = isToday(new Date(day));
      const label = today
        ? t("today")
        : t(`weekDaysShort.${format(day, "cccc").toLowerCase()}`);
      const dateLabel = `${day.getDate()} ${format(day, "MMM", {
        locale: currentLanguage === "sv-SE" && sv,
      })}`;
      const sessions = props.sessions
        .filter((session) => {
          return isSameDay(day, session.start);
        })
        .sort(sortSessionsByStartDate);

      return {
        label,
        dateLabel,
        sessions,
      };
    });

    return week.filter((day) => day.sessions.length > 0);
  }, [currentLanguage, props.sessions, t]);

  if (!ready) {
    return null;
  }

  return (
    <Grid container spacing={3}>
      {props.label && (
        <Grid item xs={12}>
          <SegmentTitle tooltip={props.tooltip}>{props.label}</SegmentTitle>
        </Grid>
      )}

      <Grid item xs={12}>
        <Divider light />

        {days.length === 0 && props.emptyText && (
          <Box py={2}>{props.emptyText}</Box>
        )}

        {days.map((day, index) => (
          <Fragment key={day.label}>
            {index !== 0 && <Divider light />}

            <Grid container>
              <Box width={70} pt="14px" pl="2px">
                <Text variant="h5" unselectable>
                  {day.label}
                </Text>
                <Text variant="body1" style={{ fontSize: "14px" }} unselectable>
                  {day.label !== "Today" && day.dateLabel}
                </Text>
              </Box>

              <Box flexGrow={1}>
                {day.sessions.map((session) => (
                  <Fragment key={session.id}>
                    <ScheduledSessionCard
                      session={session}
                      displayfavourite={false}
                    />
                  </Fragment>
                ))}
              </Box>
            </Grid>
          </Fragment>
        ))}
      </Grid>
    </Grid>
  );
};

const CustomLink: React.FC<{ to: string }> = (props) => {
  const classes = useStyles(props);
  return (
    <span className={classes.link}>
      <Link to={props.to}>{props.children}</Link>
    </span>
  );
};

const InstructorHostedSessions: React.FC = () => {
  const sessions = useInstructorSessions();
  const { t, ready } = useTranslation("account");

  if (!ready) {
    return null;
  }

  return (
    <WeekSchedule
      sessions={sessions}
      label={t("dashboard.myClasses.title")}
      tooltip={t("dashboard.myClasses.tooltip")}
      emptyText={
        <Text variant="body1">{t("dashboard.myClasses.noClassesMessage")}</Text>
      }
    />
  );
};

const ActivityDashboard: React.FC = (props) => {
  const classes = useStyles(props);
  const context = useContext(KvixUserContext);
  const [monthSelected, setMonthSelected] = useState(startOfMonth(new Date()));
  const dateString = format(monthSelected, "MMM yyyy", {
    locale: i18next.language === "sv-SE" && sv,
  });
  const [springDate, setSpringDate] = useSpring(() => ({
    to: { transform: "translateX(0%)" },
    from: { transform: "translateX(0%)" },
  }));

  return (
    <Grid container spacing={3} className={classes.activityDashboard}>
      <Grid container spacing={3} direction="row">
        <Grid item>
          <animated.div style={springDate}>
            <SegmentTitle tooltip={dateString}>{dateString}</SegmentTitle>
          </animated.div>
        </Grid>
        <Grid item>
          <KvixIcon.Chevron.Left
            onClick={() => {
              if (
                startOfMonth(parseJSON(context.user.created)) <=
                subMonths(monthSelected, 1)
              ) {
                setMonthSelected(subMonths(monthSelected, 1));
                setSpringDate({
                  reset: true,
                  to: { transform: "translateX(0%)" },
                  from: { transform: "translateX(100%)" },
                });
              }
            }}
          />
          <KvixIcon.Chevron.Right
            onClick={() => {
              if (startOfMonth(new Date()) >= addMonths(monthSelected, 1)) {
                setMonthSelected(addMonths(monthSelected, 1));
                setSpringDate({
                  reset: true,
                  to: { transform: "translateX(0%)" },
                  from: { transform: "translateX(-100%)" },
                });
              }
            }}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider light />
      </Grid>

      <Grid item xs={12}>
        <MonthActivity
          date={monthSelected}
          alwaysAnimate={!isEqual(monthSelected, startOfMonth(new Date()))}
        />
      </Grid>
    </Grid>
  );
};

export const ProfilePageDashboard: React.FC = () => {
  const context = useContext(KvixUserContext);
  const bookedSessions = context.sessions.booked;
  const { t, ready } = useTranslation("account");
  const { user } = context;

  if (!ready) {
    return null;
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <ActivityDashboard />
      </Grid>

      {isInstructor(user) && (
        <Grid item xs={12}>
          <InstructorHostedSessions />
        </Grid>
      )}

      <Grid item xs={12}>
        <WeekSchedule
          sessions={bookedSessions}
          label={t("dashboard.bookings.title")}
          tooltip={t("dashboard.bookings.tooltip")}
          emptyText={
            <Text variant="body1">
              {t("dashboard.bookings.noBookingsMessage.textBeforeLink")}
              <CustomLink to="/">
                {t("dashboard.bookings.noBookingsMessage.linkText")}
              </CustomLink>
              {t("dashboard.bookings.noBookingsMessage.textAfterLink")}
            </Text>
          }
        />
      </Grid>
    </Grid>
  );
};
